import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { joinGroupByInviteCode } from '@/api/groups';
import { supabase } from '@/lib/supabase';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type JoinState =
  | { status: 'joining' }
  | { status: 'success'; groupId: string }
  | { status: 'error'; message: string };

export function JoinGroupPage() {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<JoinState>({ status: 'joining' });
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!code || attemptedRef.current) return;
    attemptedRef.current = true;

    (async () => {
      try {
        const result = await joinGroupByInviteCode(code);
        queryClient.invalidateQueries({ queryKey: ['groups'] });
        setState({ status: 'success', groupId: result.group_id });
        setTimeout(() => navigate(`/groups/${result.group_id}`, { replace: true }), 1500);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to join group';

        // If already a member, redirect to the group
        if (message.toLowerCase().includes('already a member')) {
          const { data } = await supabase
            .from('groups')
            .select('id')
            .eq('invite_code', code.trim().toUpperCase())
            .single();

          if (data?.id) {
            setState({ status: 'success', groupId: data.id });
            setTimeout(() => navigate(`/groups/${data.id}`, { replace: true }), 1500);
            return;
          }
        }

        setState({ status: 'error', message });
      }
    })();
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === 'joining') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        <p className="mt-4 text-text-secondary">{t('web:joinGroupPage.joiningGroup')}</p>
      </div>
    );
  }

  if (state.status === 'success') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <CheckCircle className="h-12 w-12 text-green-500" />
        <p className="mt-4 text-lg font-medium text-text-primary">{t('web:joinGroupPage.joinedSuccessfully')}</p>
        <p className="mt-1 text-sm text-text-tertiary">{t('web:joinGroupPage.redirectingToGroup')}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center">
      <XCircle className="h-12 w-12 text-red-500 dark:text-red-400" />
      <p className="mt-4 text-lg font-medium text-text-primary">{t('web:joinGroupPage.failedToJoinGroup')}</p>
      <p className="mt-1 text-sm text-text-tertiary">{state.message}</p>
      <button
        onClick={() => navigate('/dashboard')}
        className="mt-6 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        {t('web:joinGroupPage.backToDashboard')}
      </button>
    </div>
  );
}
